import { useModal } from '@ebay/nice-modal-react'
import { Link, useMatch, useNavigate } from '@tanstack/router'
import axios from 'axios'
import { useEffect, useState } from 'react'
import React from 'react'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'

import avatar_3 from '../../assets/common/avatar_3.webp'
import casino_icon from '../../assets/common/casino_icon.webp'
import casino_icon_light from '../../assets/common/casino_icon_light.svg'
import coin_anim from '../../assets/common/coin_anim.gif'
import sports_icon from '../../assets/common/sports_icon.webp'
import sports_icon_light from '../../assets/common/sports_icon_light.svg'
import useGeoCheck from '../../hooks/useGeoCheck'
import useRefreshBalance from '../../hooks/useRefreshBalance'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { UtmParams } from '../../types/common'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import { numberWithCommas } from '../../utils/number'
import Icon from '../icon'
import WalletManagementModal from '../wallet-management/wallet-management-modal'
import GlobeLanguageSelector from './global-language-selector'
import LoginModal from './log-in-modal'
import LogoutModal from './log-out-modal'

interface NavbarProps {
  hideBalance?: boolean
}

const Navbar = ({ hideBalance }: NavbarProps) => {
  const match = useMatch().params as { affiliate?: string } & UtmParams
  const isTopAdVisible = useBoundStore(state => state.common.isTopAdVisible)
  const isCoinAnimVisible = useBoundStore(
    state => state.common.isCoinAnimVisible,
  )
  const pending_bind = useBoundStore(state => state.user.pending_bind)
  const profile_picture = useBoundStore(state => state.user.profile_picture)
  const setIsPendingDepositVisible = useBoundStore(
    state => state.common.setIsPendingDepositVisible,
  )
  const setProfilePicture = useBoundStore(state => state.user.setProfilePicture)
  const theme = useBoundStore(state => state.common.theme)
  const unified_balance = useBoundStore(state => state.user.unified_balance)
  const username = useBoundStore(state => state.user.username)
  const navigate = useNavigate()
  const { geoCheck } = useGeoCheck()
  const logInModal = useModal(LoginModal)
  const logOutModal = useModal(LogoutModal)
  const walletManagementModal = useModal(WalletManagementModal)
  const { t } = useTranslation(['common', 'drawer', 'walletManagement'])
  const { refreshBalance, isLoading: balanceLoading } = useRefreshBalance()
  const [profileError, setProfileError] = useState(false)
  const [affiliateId, setAffiliateId] = useState('')

  useEffect(() => {
    if (!match.affiliate) return
    const controller = new AbortController()
    const signal = controller.signal

    const fetchAffiliateId = async () => {
      try {
        const response = await api.post<ApiResponse<string>>(
          '/ajax_datav2.php',
          {
            type: 'get_agent_id',
            affiliate: match.affiliate,
          },
          { signal },
        )
        const { info, status } = response.data
        if (status === 1) {
          setAffiliateId(info)
        } else {
          navigate({ to: '/' })
        }
      } catch (error) {
        navigate({ to: '/' })
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchAffiliateId()

    return () => {
      controller.abort()
    }
  }, [username])

  useEffect(() => {
    if (!username) return
    refreshBalance()
    const controller = new AbortController()
    const signal = controller.signal

    const fetchProfilePicture = async () => {
      try {
        const response = await api.post<ApiResponse<string>>(
          '/ajax_data.php',
          {
            type: 'get_imgurl',
          },
          { signal },
        )
        const { status, info } = response.data
        if (status === 1) {
          setProfilePicture(info)
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchProfilePicture()

    return () => {
      controller.abort()
    }
  }, [username])

  return (
    <div
      className="flx-btw-ctr w-full bg-primary shadow-[0px_1px_5px_rgba(0,0,0,0.1)] sticky z-[100] h-[70px] px-10 py-0 top-0 xl:px-[90px] 2xl:px-[150px] 2xl:py-2.5 3xl:px-[250px] 3xl:py-2.5"
      style={{ top: isTopAdVisible ? 38 : 0 }}>
      <div className="flx-ctr" style={{ justifyContent: 'flex-start' }}>
        <Link to="/" className="flx-ctr min-w-[78px]">
          <img
            className="w-[120px] aspect-[177_/_80]"
            src={`https://www.u2d8899.com/mexicoimages/logo/${theme}.png`}
            alt="Company Logo"
          />
        </Link>
      </div>
      <div className="flx-btw-ctr px-6 w-[600px]">
        <Link
          to={
            +pending_bind === 0 || +pending_bind === 2 ? undefined : '/sports'
          }>
          <button
            className="relative pb-2.5 hover:opacity-70"
            onClick={() => {
              if (+pending_bind === 0 || +pending_bind === 2)
                setIsPendingDepositVisible(true)
            }}>
            <img
              className="max-h-[30px] w-full"
              src={theme === 'dark' ? sports_icon : sports_icon_light}
              alt="Sports"
              style={{ aspectRatio: '859 / 210' }}
            />
            <span className="font-Syncopate-Bold whitespace-nowrap absolute -translate-x-2/4 uppercase text-[7px] leading-[0] text-txt tracking-[1px] left-2/4 bottom-1">
              {t('sports', { ns: 'drawer' })}
            </span>
          </button>
        </Link>
        <Link to="/casino/$category" params={{ category: 'lobby' }}>
          <button className="relative pb-2.5 hover:opacity-70">
            <img
              className="max-h-[30px] w-full"
              src={theme === 'dark' ? casino_icon : casino_icon_light}
              alt="Casino"
              style={{ aspectRatio: '362 / 105' }}
            />
            <span className="font-Syncopate-Bold whitespace-nowrap absolute -translate-x-2/4 uppercase text-[7px] leading-[0] text-txt tracking-[1px] left-2/4 bottom-1">
              {t('casino', { ns: 'drawer' })}
            </span>
          </button>
        </Link>
        <Link to="/promotions">
          <button className="relative pb-2.5 hover:opacity-70 text-xl text-txt uppercase font-Geometr706-BlkCn-BT">
            {t('promotions', { ns: 'drawer' })}
          </button>
        </Link>
      </div>
      <div className="flx-ctr" style={{ justifyContent: 'flex-end' }}>
        {username ? (
          <>
            {!hideBalance && (
              <>
                <div
                  style={{ cursor: 'pointer' }}
                  className="flx-ctr"
                  onClick={() =>
                    walletManagementModal.show({
                      walletManagementDefaultTab: { tab: 0 },
                    })
                  }>
                  <div className="relative mr-1.5 -mt-[6px]">
                    <Icon
                      className="fill-txt"
                      id="wallet"
                      width="22"
                      height="22"
                    />
                    {isCoinAnimVisible && (
                      <img
                        className="absolute left-[-165px] w-[150px] animate-fadeIn pointer-events-none rotate-[30deg] -top-8"
                        src={coin_anim}
                      />
                    )}
                  </div>
                  <span className="text-base text-txt">
                    ${numberWithCommas(parseFloat(unified_balance))}
                  </span>
                </div>
                <button
                  onClick={refreshBalance}
                  className={classNames(
                    'flex mr-[9px] p-1.5',
                    balanceLoading && 'animate-rotate2',
                  )}>
                  <Icon
                    id="refresh-balance"
                    width="17"
                    height="17"
                    fill="#D2D4D6"
                  />
                </button>
              </>
            )}
            <button
              className="text-center text-xs font-Syncopate-Bold transition-all duration-[0.2s] ease-[ease-in-out] block uppercase px-[17px] py-[8px] rounded-sm bg-[#df0033] text-white"
              style={{ marginRight: '15px' }}
              onClick={() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'deposit_form_open',
                    event_category: 'deposit_funds',
                    event_action: 'click_open',
                    event_label: 'Deposit form Open',
                    user_id: username,
                  },
                })
                walletManagementModal.show({
                  walletManagementDefaultTab: { page: 0, tab: 0 },
                })
              }}>
              {t('deposit', { ns: 'drawer' })}
            </button>
            <Link to="/profile">
              <div className="w-8 h-8 bg-transparent overflow-hidden bg-white mr-[15px] rounded-[50%] border border-solid border-[#ccc] dark:border-white">
                <img
                  className="w-[30px] h-[30px] object-cover"
                  src={
                    profile_picture && !profileError
                      ? profile_picture
                      : avatar_3
                  }
                  alt="Avatar"
                  onError={() => setProfileError(true)}
                />
              </div>
            </Link>
            <button
              className="flx-ctr fill-txt"
              style={{ cursor: 'pointer' }}
              onClick={() => logOutModal.show()}>
              <Icon id="logout" width="22" height="20" />
            </button>
          </>
        ) : (
          <>
            <button
              className="flex items-center text-[10px] text-txt transition-all duration-[0.2s] ease-[ease-in-out] uppercase mr-4 font-Syncopate-Bold"
              onClick={() => geoCheck(logInModal.show)}>
              <Icon
                className="fill-txt mr-2"
                id="login"
                width="16"
                height="15"
              />
              {t('login', { ns: 'common' })}
            </button>
            <button
              onClick={() =>
                geoCheck(() =>
                  navigate({
                    to: '/register',
                    search: { ...match, referral: affiliateId },
                  }),
                )
              }>
              <span className="flx-ctr h-[35px] bg-[#df0033] px-4 py-0 rounded-sm">
                <p className="text-[10px] text-white uppercase font-Syncopate-Bold">
                  {t('register', { ns: 'common' })}
                </p>
              </span>
            </button>
          </>
        )}
        <GlobeLanguageSelector />
      </div>
    </div>
  )
}

export default Navbar
